import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, ImagePlus, X } from "lucide-react";
import { api, imgSrc } from "../lib/api";

const EMPTY = {
    nome: "",
    marca: "",
    categoria: "Elettrodomestici",
    prezzo: "",
    descrizione: "",
    immagine: "",
    wa_link: "",
};

const inputCls =
    "w-full bg-background border border-line px-4 py-3 text-sm focus:outline-none focus:border-primary transition-colors placeholder:text-muted-foreground/60";

export default function ProdottiPanel() {
    const { data: prodotti, isLoading } = useQuery({
        queryKey: ["admin-prodotti"],
        queryFn: async () => (await api.get("/prodotti")).data,
    });
    const qc = useQueryClient();
    const [form, setForm] = useState(EMPTY);
    const [editingId, setEditingId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const uploadFoto = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const res = await api.post("/admin/upload", fd);
            setForm((f) => ({ ...f, immagine: res.data.url }));
            toast.success("Foto caricata!");
        } catch {
            toast.error("Caricamento foto non riuscito.");
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const salva = async (e) => {
        e.preventDefault();
        if (!form.nome.trim() || !form.marca.trim() || !form.prezzo) {
            toast.error("Compila nome, marca e prezzo.");
            return;
        }
        setSaving(true);
        const payload = { ...form, prezzo: parseFloat(form.prezzo) };
        try {
            if (editingId) {
                await api.put(`/admin/prodotti/${editingId}`, payload);
                toast.success("Prodotto aggiornato!");
            } else {
                await api.post("/admin/prodotti", payload);
                toast.success("Prodotto aggiunto!");
            }
            setForm(EMPTY);
            setEditingId(null);
            qc.invalidateQueries({ queryKey: ["admin-prodotti"] });
            qc.invalidateQueries({ queryKey: ["prodotti"] });
        } catch {
            toast.error("Salvataggio non riuscito.");
        } finally {
            setSaving(false);
        }
    };

    const modifica = (p) => {
        setEditingId(p.id);
        setForm({
            nome: p.nome,
            marca: p.marca,
            categoria: p.categoria,
            prezzo: String(p.prezzo),
            descrizione: p.descrizione || "",
            immagine: p.immagine || "",
            wa_link: p.wa_link || "",
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const elimina = async (p) => {
        if (!window.confirm(`Eliminare "${p.nome}"?`)) return;
        try {
            await api.delete(`/admin/prodotti/${p.id}`);
            toast.success("Prodotto eliminato.");
            qc.invalidateQueries({ queryKey: ["admin-prodotti"] });
            qc.invalidateQueries({ queryKey: ["prodotti"] });
        } catch {
            toast.error("Eliminazione non riuscita.");
        }
    };

    const annulla = () => {
        setEditingId(null);
        setForm(EMPTY);
    };

    return (
        <div className="space-y-10">
            <section className="border border-line bg-card p-6 md:p-8">
                <h2 className="font-display text-2xl font-extrabold tracking-tight flex items-center gap-3">
                    <Plus className="h-5 w-5 text-primary" />
                    {editingId ? "Modifica prodotto" : "Nuovo prodotto"}
                </h2>
                <form onSubmit={salva} className="mt-6 grid sm:grid-cols-2 gap-4" data-testid="admin-product-form">
                    <input data-testid="admin-product-nome" className={inputCls} placeholder="Nome prodotto *" value={form.nome} onChange={set("nome")} />
                    <input data-testid="admin-product-marca" className={inputCls} placeholder="Marca (es. Vorwerk, Candy) *" value={form.marca} onChange={set("marca")} />
                    <select data-testid="admin-product-categoria" className={inputCls} value={form.categoria} onChange={set("categoria")}>
                        <option value="Elettrodomestici">Elettrodomestici</option>
                        <option value="Vorwerk Folletto">Vorwerk Folletto</option>
                    </select>
                    <input data-testid="admin-product-prezzo" className={inputCls} type="number" step="0.01" min="0" placeholder="Prezzo € *" value={form.prezzo} onChange={set("prezzo")} />
                    <textarea
                        data-testid="admin-product-descrizione"
                        className={`${inputCls} sm:col-span-2 resize-none`}
                        rows={2}
                        placeholder="Descrizione breve"
                        value={form.descrizione}
                        onChange={set("descrizione")}
                    />
                    <input
                        data-testid="admin-product-walink"
                        className={`${inputCls} sm:col-span-2`}
                        placeholder="Link catalogo WhatsApp (opzionale) — es. https://wa.me/p/XXXXXXXXX/YYYY"
                        value={form.wa_link}
                        onChange={set("wa_link")}
                    />
                    <p className="sm:col-span-2 text-xs leading-relaxed text-muted-foreground">
                        Come trovare il link: nell'app WhatsApp Business apri
                        Strumenti aziendali → Catalogo → tocca il prodotto →
                        condividi → copia il link. Incollalo qui e il pulsante
                        WhatsApp di questo prodotto aprirà direttamente la sua
                        scheda in chat.
                    </p>

                    <div className="sm:col-span-2 flex flex-wrap items-center gap-4">
                        {form.immagine && (
                            <div className="relative">
                                <img
                                    src={imgSrc(form.immagine)}
                                    alt="Anteprima"
                                    className="h-20 w-20 object-cover border border-line"
                                />
                                <button
                                    type="button"
                                    onClick={() => setForm({ ...form, immagine: "" })}
                                    aria-label="Rimuovi foto"
                                    className="absolute -top-2 -right-2 bg-foreground text-background p-1"
                                >
                                    <X className="h-3 w-3" />
                                </button>
                            </div>
                        )}
                        <input
                            ref={fileRef}
                            type="file"
                            accept="image/*"
                            data-testid="admin-upload-foto"
                            onChange={uploadFoto}
                            className="hidden"
                            id="admin-foto-input"
                        />
                        <label
                            htmlFor="admin-foto-input"
                            className="inline-flex items-center gap-2 border border-line px-4 py-3 font-mono text-xs tracking-[0.15em] uppercase cursor-pointer hover:border-primary hover:text-primary transition-colors"
                        >
                            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                            {uploading ? "Carico…" : "Carica foto"}
                        </label>
                        <div className="ml-auto flex gap-3">
                            {editingId && (
                                <button type="button" onClick={annulla} data-testid="admin-product-annulla" className="border border-line px-5 py-3 font-display font-bold text-sm hover:border-foreground/40 transition-colors">
                                    Annulla
                                </button>
                            )}
                            <button type="submit" data-testid="admin-product-save" disabled={saving} className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 font-display font-bold text-sm tracking-tight hover:bg-primary/90 transition-colors disabled:opacity-60">
                                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                                {editingId ? "Salva modifiche" : "Aggiungi prodotto"}
                            </button>
                        </div>
                    </div>
                </form>
            </section>

            <section>
                <h2 className="font-display text-2xl font-extrabold tracking-tight">
                    Catalogo ({prodotti?.length ?? 0})
                </h2>
                {isLoading ? (
                    <p className="mt-4 font-mono text-xs uppercase tracking-widest text-muted-foreground">Caricamento…</p>
                ) : (
                    <div className="mt-4 space-y-3">
                        {prodotti?.map((p) => (
                            <article key={p.id} data-testid={`admin-product-row-${p.id}`} className="flex items-center gap-4 border border-line bg-card p-3">
                                <img src={imgSrc(p.immagine)} alt={p.nome} className="h-14 w-14 object-cover border border-line shrink-0" />
                                <div className="min-w-0 flex-1">
                                    <p className="font-display font-bold text-sm truncate">{p.nome}</p>
                                    <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-muted-foreground mt-0.5">
                                        {p.marca} · {p.categoria} · € {p.prezzo.toFixed(2).replace(".", ",")}
                                    </p>
                                </div>
                                <button
                                    data-testid={`admin-product-edit-${p.id}`}
                                    onClick={() => modifica(p)}
                                    aria-label={`Modifica ${p.nome}`}
                                    className="p-2.5 border border-line hover:border-primary hover:text-primary transition-colors"
                                >
                                    <Pencil className="h-4 w-4" />
                                </button>
                                <button
                                    data-testid={`admin-product-delete-${p.id}`}
                                    onClick={() => elimina(p)}
                                    aria-label={`Elimina ${p.nome}`}
                                    className="p-2.5 border border-line hover:border-destructive hover:text-destructive transition-colors"
                                >
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            </article>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}